import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Download, GetApp } from '@mui/icons-material';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { ClientSummaryTable } from './ClientSummaryTable';
import { s3Service } from '../services/s3Service';
import { downloadService } from '../services/downloadService';
import { parseCSV, CSVData } from '../utils/csvParser';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Hardcoded ICE clients
const ICE_CLIENTS = [
  'VIZZA',
  'NAVNIT',
  'HARITHA',
  'CERTIGO',
  'SPINNY',
  'INSUGO',
  'SSS',
  'RELIGARE',
  'SHRINITHI',
  'RISHABH',
  'KATARIA',
  'NETAMBIT',
  'PURPLEPOND',
  'OPP',
  'BFSI',
  'MUTHOOT',
  'SAFERISK',
  'VIAANSH',
  'ADVANCE_LIFE',
  'MERA_BIMA',
  'SYNERGY'
];

interface IceDashboardProps {
  fontFamily?: string;
  onNavigateToHomepage: () => void;
}

// Add type conversion helper
const toNumber = (value: any): number => {
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

export const IceDashboard: React.FC<IceDashboardProps> = ({ 
  fontFamily = 'JetBrains Mono',
  onNavigateToHomepage
}) => {
  const currentDate = new Date();
  const [clientData, setClientData] = useState<CSVData[]>([]);
  const [networkData, setNetworkData] = useState<CSVData[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showAllClients, setShowAllClients] = useState(true);
  const [selectedClient, setSelectedClient] = useState<string>('');

  const [selectedMonth, setSelectedMonth] = useState<string>((currentDate.getMonth() + 1).toString().padStart(2, '0'));
  const [selectedYear, setSelectedYear] = useState<string>(currentDate.getFullYear().toString());

  // Load data on initial mount
  useEffect(() => {
    handleMonthYearSelection(selectedMonth, selectedYear);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const yearCurr = new Date().getFullYear();

  const yearOptions = [yearCurr,yearCurr-1,yearCurr-2,yearCurr-3,yearCurr-4,yearCurr-5,yearCurr-6,yearCurr-7,yearCurr-8,yearCurr-9,yearCurr-10];

  const handleMonthYearSelection = async (month: string, year: string) => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    // Construct folder name: logs_ice_in_MM-YYYY
    const folder = `logs_ice_in_${month}-${year}`;
    setSelectedFolder(folder);
    
    // Get month name for display
    const monthName = monthNames[parseInt(month) - 1];
    
    try {
      // Load client summary and network data
      const clientSummaryResponse = await s3Service.getFileContent(`ICE-VEHICLE-LOOKUP/${year}/${month}/client_summary.csv`);
      const networkAllResponse = await s3Service.getFileContent(`ICE-VEHICLE-LOOKUP/${year}/${month}/network_all.csv`);
      
      if (!clientSummaryResponse && !networkAllResponse) {
        setError(`🔴 No ICE data found for ${monthName} ${year}.`);
        setClientData([]);
        setNetworkData([]);
        return;
      }
      
      // Parse CSV content
      const parsedClientData = clientSummaryResponse ? parseCSV(clientSummaryResponse) : [];
      const parsedNetworkData = networkAllResponse ? parseCSV(networkAllResponse) : [];
      
      // Update state
      setClientData(parsedClientData);
      setNetworkData(parsedNetworkData);
      setSuccess(`✅ Showing ICE vehicle lookup data for ${monthName} ${year}`);
    } catch (error: any) {
      console.error('Error loading ICE dashboard data:', error);
      setError(`🔴 Error loading data for ${monthName} ${year}: ${error.message}`);
      setClientData([]);
      setNetworkData([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on selected client
  const filteredClientData = showAllClients ? clientData : clientData.filter(row => row.client_name === selectedClient);
  const filteredNetworkData = showAllClients ? networkData : networkData.filter(row => row.sub_product === selectedClient);
  // print column names


  // Add Pie chart import
  // Add Pie chart import

  // Inside the component, update the chart options
  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          font: {
            family: fontFamily,
            size: 12
          },
          padding: 20 // Add padding to prevent text cutoff
        }
      }
    }
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            family: fontFamily,
            size: 12
          },
          padding: 20 // Add padding to prevent text cutoff
        }
      }
    },
    scales: {
      x: {
        ticks: {
          font: {
            family: fontFamily
          }
        }
      },
      y: {
        ticks: {
          font: {
            family: fontFamily
          }
        }
      }
    }
  };

  return (
    <Container maxWidth="xl" sx={{ fontFamily }}>
      <Box sx={{ my: 4, fontFamily }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Button
            variant="outlined"
            onClick={onNavigateToHomepage}
            sx={{ fontFamily }}
          >
            ← Back to Home
          </Button>
          
          <Typography variant="h4" component="h1" sx={{ fontFamily, flexGrow: 1, textAlign: 'center' }}>
            🚗 ICE Vehicle Lookup Dashboard
          </Typography>
        </Box>

        {/* Month/Year Selector */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa', fontFamily }}>
          <Typography variant="h6" gutterBottom sx={{ fontFamily, mb: 2 }}>
            📊 Select Reporting Period
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <FormControl fullWidth>
                <InputLabel sx={{ fontFamily }}>Select Month</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Select Month"
                  onChange={(e) => {
                    const month = e.target.value;
                    setSelectedMonth(month);
                    if (month && selectedYear) {
                      handleMonthYearSelection(month, selectedYear);
                    }
                  }}
                  sx={{ fontFamily }}
                >
                  {monthNames.map((monthName, index) => (
                    <MenuItem 
                      key={index + 1} 
                      value={(index + 1).toString().padStart(2, '0')} 
                      sx={{ fontFamily }}
                    >
                      {monthName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Box>
              <FormControl fullWidth>
                <InputLabel sx={{ fontFamily }}>Select Year</InputLabel>
                <Select
                  value={selectedYear}
                  label="Select Year"
                  onChange={(e) => {
                    const year = e.target.value;
                    setSelectedYear(year);
                    if (selectedMonth && year) {
                      handleMonthYearSelection(selectedMonth, year);
                    }
                  }}
                  sx={{ fontFamily }}
                >
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year.toString()} sx={{ fontFamily }}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Paper>

        {/* Client Filter */}
        {clientData.length > 0 && (
          <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showAllClients}
                    onChange={(e) => {
                      setShowAllClients(e.target.checked);
                      if (e.target.checked) {
                        setSelectedClient('');
                      } else if (!selectedClient) {
                        setSelectedClient(ICE_CLIENTS[0]);
                      }
                    }}
                  />
                }
                label="Show All Clients"
                sx={{ fontFamily }}
              />
              
              {!showAllClients && (
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel sx={{ fontFamily }}>Select Client</InputLabel>
                  <Select
                    value={selectedClient}
                    label="Select Client"
                    onChange={(e) => setSelectedClient(e.target.value)}
                    sx={{ fontFamily }}
                  >
                    {ICE_CLIENTS.map((client) => (
                      <MenuItem key={client} value={client} sx={{ fontFamily }}>
                        {client}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Paper>
        )}

        {/* Status Messages */}
        {loading && (
          <Alert severity="info" sx={{ mb: 3, fontFamily }}>
            Loading dashboard data...
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 3, fontFamily }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3, fontFamily }}>
            {success}
          </Alert>
        )}

        {/* Download Raw Files Section */}
        {selectedFolder && (
          <Paper elevation={2} sx={{ p: 2, mb: 3, backgroundColor: '#f8f9fa' }}>
            <Typography variant="h6" gutterBottom sx={{ fontFamily }}>
              📥 Download Raw Data Files
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={() => downloadService.downloadFromS3(`ICE-VEHICLE-LOOKUP/${selectedYear}/${selectedMonth}`, 'network_all.csv')}
                sx={{ fontFamily }}
                color="primary"
              >
                All Network Logs
              </Button>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={() => downloadService.downloadFromS3(`ICE-VEHICLE-LOOKUP/${selectedYear}/${selectedMonth}`, 'network_success.csv')}
                sx={{ fontFamily }}
                color="primary"
              >
                Success Logs
              </Button>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={() => downloadService.downloadFromS3(`ICE-VEHICLE-LOOKUP/${selectedYear}/${selectedMonth}`, 'network_failure.csv')}
                sx={{ fontFamily }}
                color="primary"
              >
                Failure Logs
              </Button>
              <Button
                variant="outlined"
                startIcon={<GetApp />}
                onClick={() => downloadService.downloadAllFiles(`ICE-VEHICLE-LOOKUP/${selectedYear}/${selectedMonth}`)}
                sx={{ fontFamily }}
              >
                Download All Files
              </Button>
            </Box>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontFamily }}>
              💡 Download raw log files directly from S3 for detailed analysis
            </Typography>
          </Paper>
        )}

        {/* Dashboard Content */}
        {(clientData.length > 0 || networkData.length > 0) && (
          <>
            {/* Client Summary Table */}
            {filteredClientData.length > 0 && (
              <ClientSummaryTable data={filteredClientData} fontFamily={fontFamily} />
            )}

            {/* Overall Success/Failure Distribution - Pie Chart */}
            {filteredClientData.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Paper elevation={3} sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontFamily }}>
                    📊 Overall Performance Summary
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: showAllClients ? { xs: '1fr', md: '1fr 1fr' } : '1fr', gap: 3 }}>
                    <Box>
                      <Typography variant="subtitle1" align="center" sx={{ fontFamily, mb: 2 }}>
                        Success vs Failure Distribution
                      </Typography>
                      <Box sx={{ height: 450, position: 'relative', mb: 2 }}> {/* Increased height and added margin */}
                        <Pie
                          options={pieOptions}
                          data={{
                            labels: ['Successful', 'Failed'],
                            datasets: [{
                              data: [
                                filteredClientData.reduce((sum, row) => sum + toNumber(row.successful_requests), 0),
                                filteredClientData.reduce((sum, row) => sum + toNumber(row.failed_requests), 0)
                              ],
                              backgroundColor: ['#4CAF50', '#f44336'],
                              borderColor: ['#388E3C', '#D32F2F'],
                              borderWidth: 1
                            }]
                          }}
                        />
                      </Box>
                    </Box>
                    {showAllClients && (
                      <Box>
                        <Typography variant="subtitle1" align="center" sx={{ fontFamily, mb: 2 }}>
                          Client-wise Success Rate
                        </Typography>
                        <Box sx={{ height: 450, position: 'relative', mb: 2 }}> {/* Increased height and added margin */}
                          <Bar
                            options={barOptions}
                            data={{
                              labels: filteredClientData.map(row => String(row.client_name)),
                              datasets: [{
                                label: 'Success Rate (%)',
                                data: filteredClientData.map(row => {
                                  const total = toNumber(row.total_requests);
                                  const success = toNumber(row.successful_requests);
                                  return total > 0 ? Number(((success / total) * 100).toFixed(1)) : 0;
                                }),
                                backgroundColor: '#2196f3',
                                borderColor: '#1976d2',
                                borderWidth: 1
                              }]
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Box>
            )}

            {/* Network Performance Analysis */}
            {networkData.length > 0 && (
              <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontFamily }}>
                  🌐 Network Performance Analysis
                </Typography>
                <Box sx={{ height: 450, position: 'relative', mb: 2 }}>
                  <Typography variant="subtitle1" align="center" sx={{ fontFamily, mb: 2 }}>
                    Response Time Distribution {!showAllClients && `for ${selectedClient}`}
                  </Typography>
                  <Pie
                    options={pieOptions}
                    data={{
                      labels: ['< 500ms','500ms-1s', '1-2s', '2-5s', '> 5s'],
                      datasets: [{
                        data: [
                          filteredNetworkData.filter(row => toNumber(row.latency_ms) < 500).length,
                          filteredNetworkData.filter(row => toNumber(row.latency_ms) >= 500 && toNumber(row.latency_ms) < 1000).length,
                          filteredNetworkData.filter(row => toNumber(row.latency_ms) >= 1000 && toNumber(row.latency_ms) < 2000).length,
                          filteredNetworkData.filter(row => toNumber(row.latency_ms) >= 2000 && toNumber(row.latency_ms) < 5000).length,
                          filteredNetworkData.filter(row => toNumber(row.latency_ms) >= 5000).length
                        ],
                        backgroundColor: ['#4CAF50', '#8BC34A', '#FFA726', '#f44336', '#9C27B0']
                      }]
                    }}
                  />
                </Box>
              </Paper>
            )}

            {/* Client-wise Request Volume - Bar Chart */}
            {showAllClients && filteredClientData.length > 0 && (
              <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontFamily }}>
                  📈 Client-wise Request Volume
                </Typography>
                <Box sx={{ height: 450, position: 'relative', mb: 2 }}> {/* Increased height and added margin */}
                  <Bar
                    options={barOptions}
                    data={{
                      labels: filteredClientData.map(row => String(row.client_name)),
                      datasets: [
                        {
                          label: 'Successful Requests',
                          data: filteredClientData.map(row => toNumber(row.successful_requests)),
                          backgroundColor: '#4CAF50',
                        },
                        {
                          label: 'Failed Requests',
                          data: filteredClientData.map(row => toNumber(row.failed_requests)),
                          backgroundColor: '#f44336',
                        }
                      ]
                    }}
                  />
                </Box>
              </Paper>
            )}
          </>
        )}
      </Box>
    </Container>
  );
}; 